/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'

import Toast from 'components/atoms/Toast'
import config from 'config/app'

interface TestWebhookFormProps {
  refetchOrder?: () => void
}

export const TestWebhookForm = ({ refetchOrder }: TestWebhookFormProps) => {
  const [testTrackingNumber, setTestTrackingNumber] = useState('')
  const [testWebhookStatus, setTestWebhookStatus] = useState('TRANSIT')
  const [isSendingWebhook, setIsSendingWebhook] = useState(false)

  const handleSendTestWebhook = async () => {
    if (!testTrackingNumber.trim()) {
      Toast({
        type: 'error',
        label: 'Please enter a transaction ID',
      })
      return
    }

    setIsSendingWebhook(true)
    try {
      const now = new Date().toISOString()
      const webhookPayload = {
        event: 'track_updated',
        test: false,
        data: {
          address_from: {
            city: 'Swarthmore',
            state: 'PA',
            zip: '19081',
            country: 'US',
          },
          address_to: {
            city: 'Lake City',
            state: 'FL',
            zip: '32055',
            country: 'US',
          },
          tracking_status: {
            object_created: '2025-05-14T21:07:27.973Z',
            object_updated: now,
            object_id: testTrackingNumber,
            status: testWebhookStatus,
            status_details: 'Test webhook from frontend',
            status_date: now,
            substatus: {
              code: 'information_received',
              text: 'Test webhook',
              action_required: false,
            },
            location: null,
          },
          tracking_number: '9202090182632214586815',
          tracking_history: [
            {
              object_created: '2024-05-14T21:07:27.973Z',
              object_updated: now,
              object_id: testTrackingNumber,
              status: testWebhookStatus,
              status_details: 'Test webhook from frontend',
              status_date: now,
              substatus: {
                code: 'information_received',
                text: 'Test webhook',
                action_required: false,
              },
              location: null,
            },
          ],
          carrier: 'usps',
          eta: null,
          original_eta: null,
          servicelevel: {
            token: 'usps_first',
            name: 'FC Package',
          },
          metadata: null,
          transaction: testTrackingNumber,
          messages: [],
          test: false,
        },
      }

      const response = await fetch(
        `${config?.newApi || 'http://localhost:3001'}/lab/hooks/shippo`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify(webhookPayload),
        },
      )

      if (response.ok) {
        Toast({
          type: 'success',
          label: `Order status updated to ${testWebhookStatus}. SMS, email, and scheduling note sent successfully.`,
        })
        if (refetchOrder) {
          refetchOrder()
        }
      } else {
        const errorText = await response.text()
        Toast({
          type: 'error',
          label: `Failed to update order status: ${errorText}`,
        })
      }
    } catch (error: any) {
      Toast({
        type: 'error',
        label: `Error updating order status: ${error?.message || 'Unknown error'}`,
      })
    } finally {
      setIsSendingWebhook(false)
    }
  }

  return (
    <div
      css={{
        marginTop: 40,
        padding: 20,
        border: '2px dashed #ff9800',
        borderRadius: 8,
        backgroundColor: '#fff3e0',
      }}>
      <h5 className="font-weight-600" css={{ color: '#e65100', marginBottom: 15 }}>
        Test Order Status Update (Dev/Local Only)
      </h5>
      <div
        css={{
          padding: 12,
          marginBottom: 20,
          backgroundColor: '#fff',
          borderRadius: 6,
          border: '1px solid #ffb74d',
        }}>
        <p css={{ fontSize: 13, color: '#856404', margin: 0, lineHeight: 1.6 }}>
          <strong>What this does:</strong> Simulates a Shippo webhook to update the order status to
          TRANSIT or DELIVERED. This will update the order status, send SMS and email notifications
          to the patient, and create a scheduling note in the order log.
        </p>
      </div>
      <div className="d-flex align-items-end" css={{ gap: 15 }}>
        <Form.Group css={{ flex: 1, marginBottom: 0 }}>
          <Form.Label css={{ fontWeight: 600, fontSize: 14 }}>
            Transaction ID (from Network Tab)
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter transaction ID of this order from network tab (e.g., 47086dfedcae4402a73b871a34023400)"
            value={testTrackingNumber}
            onChange={(e) => setTestTrackingNumber(e.target.value)}
          />
        </Form.Group>
        <Form.Group css={{ width: 200, marginBottom: 0, marginLeft: 15 }}>
          <Form.Label css={{ fontWeight: 600, fontSize: 14 }}>Update Status To</Form.Label>
          <Form.Select
            value={testWebhookStatus}
            onChange={(e) => setTestWebhookStatus(e.target.value)}
            style={{ marginLeft: 12, paddingTop: 6, paddingBottom: 6 }}>
            <option value="TRANSIT">TRANSIT</option>
            <option value="DELIVERED">DELIVERED</option>
          </Form.Select>
        </Form.Group>
        <Button
          variant="warning"
          onClick={handleSendTestWebhook}
          disabled={isSendingWebhook}
          css={{ height: 38 }}>
          {isSendingWebhook ? 'Updating Status...' : 'Update Order Status'}
        </Button>
      </div>
    </div>
  )
}
